import {useEffect, useRef, useState} from 'react';
import * as THREE from 'three';
import {STLLoader} from 'three/examples/jsm/loaders/STLLoader';
import {PLYLoader} from 'three/examples/jsm/loaders/PLYLoader';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls';
import {Spinner} from 'react-bootstrap';

export default function ModelViewer({url, fileType = 'stl', style, isThumbnail = false}) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url || !containerRef.current) return;

    let animationFrameId;
    let renderer;
    let controls;
    let currentMesh = null;
    let isMounted = true;

    setLoading(true);
    setError(null);

    const container = containerRef.current;
    const width = container.clientWidth || 300;
    const height = container.clientHeight || 300;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf9fafb);

    // Camera setup
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 0, 100);

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const dirLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight1.position.set(1, 1, 1).normalize();
    scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    dirLight2.position.set(-1, -1, -1).normalize();
    scene.add(dirLight2);

    // Renderer
    renderer = new THREE.WebGLRenderer({antialias: true, alpha: true});
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    container.appendChild(renderer.domElement);

    // Controls
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.autoRotate = true;
    controls.autoRotateSpeed = isThumbnail ? 0.5 : 1.5;
    controls.enableZoom = !isThumbnail;
    controls.enablePan = !isThumbnail;

    // Loader resolution
    const isPly =
      fileType === 'ply' || url.toLowerCase().includes('.ply') || url.toLowerCase().includes('model/ply');
    const loader = isPly ? new PLYLoader() : new STLLoader();

    loader.load(
      url,
      (geometry) => {
        if (!isMounted) return;

        geometry.computeVertexNormals();
        geometry.center();

        geometry.computeBoundingSphere();
        const sphere = geometry.boundingSphere;
        const radius = sphere ? sphere.radius : 50;

        // Primary brand color material (#C43B71)
        const material = new THREE.MeshStandardMaterial({
          color: 0xc43b71,
          metalness: 0.2,
          roughness: 0.4,
          side: THREE.DoubleSide,
        });

        currentMesh = new THREE.Mesh(geometry, material);
        scene.add(currentMesh);

        // Adjust camera position based on bounding sphere radius
        const fov = camera.fov * (Math.PI / 180);
        let distance = Math.abs(radius / Math.sin(fov / 2)) * 1.3;
        distance = Math.max(distance, 10);
        camera.position.set(0, 0, distance);
        camera.lookAt(0, 0, 0);
        controls.update();

        setLoading(false);
      },
      undefined,
      (err) => {
        if (!isMounted) return;
        console.error('Failed to load 3D model:', err);
        setError('Failed to load 3D model');
        setLoading(false);
      },
    );

    // Animation loop
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      if (controls) controls.update();
      if (renderer && scene && camera) renderer.render(scene, camera);
    };
    animate();

    // Handle container resize
    const handleResize = () => {
      if (!containerRef.current || !renderer || !camera) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };

    const resizeObserver = new ResizeObserver(() => handleResize());
    resizeObserver.observe(container);

    // Clean up WebGL resources on unmount
    return () => {
      isMounted = false;
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
      if (controls) controls.dispose();
      if (currentMesh) {
        if (currentMesh.geometry) currentMesh.geometry.dispose();
        if (currentMesh.material) currentMesh.material.dispose();
      }
      if (renderer) {
        if (renderer.domElement && renderer.domElement.parentNode) {
          renderer.domElement.parentNode.removeChild(renderer.domElement);
        }
        renderer.dispose();
      }
    };
  }, [url, fileType, isThumbnail]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: 300,
        background: '#F9FAFB',
        borderRadius: 12,
        overflow: 'hidden',
        ...style,
      }}
    >
      {loading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#F9FAFB',
            zIndex: 2,
          }}
        >
          <Spinner animation="border" style={{color: '#C43B71'}} />
        </div>
      )}
      {error && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#DC2626',
            fontSize: 14,
            fontWeight: 600,
            zIndex: 2,
          }}
        >
          {error}
        </div>
      )}
      <div ref={containerRef} style={{width: '100%', height: '100%'}} />
    </div>
  );
}
